/**
 * The photoreal 360: which wall it opens on. Rendered from the room's centre
 * at eye height, level, its middle straight on one wall (render_room.py
 * panorama_camera; the viewer's export puts the camera there, Room3DViewer
 * exportScene):
 *   - a garage, workshop or shed (and any space with no rule of its own): the
 *     wall with a garage door — the one with the most garage door when
 *     several have one;
 *   - a space whose kit names `feature` props (staging/kits.ts: a bathroom's
 *     vanity and toilet, a laundry room's washer and dryer, a living space's
 *     TV console): the wall most of them stand against;
 * and the front wall whenever none of that is there (no garage door, the
 * room not staged).
 */

import type { RoomSurfaces } from '../../../types/sections';
import type { StagedPropView } from './stagingMeshes';
import type { WallFace } from './types';
import { STAGING_KITS } from '../../../calculations/staging/kits';

/** `RoomSurfaces.walls` order. */
const FACES: WallFace[] = ['front', 'right', 'back', 'left'];

/** The staged props a space's 360 faces, by space type; the rest face a garage door. */
const FACE_PROPS: Record<string, string[]> = {
  washroom: ['vanity', 'toilet'],
  'laundry-room': ['washer', 'dryer'],
  'living-space': ['console'],
  basement: ['console'],
};

/** The wall a floor prop stands against, from the yaw staging gave it (stage.ts rotationFor). */
function faceOfRotation(rotation: number): WallFace {
  const r = Math.atan2(Math.sin(rotation), Math.cos(rotation));
  if (Math.abs(r) < 0.1) return 'front';
  if (Math.abs(Math.abs(r) - Math.PI) < 0.1) return 'back';
  return r > 0 ? 'left' : 'right';
}

export function panoramaFace(spaceType: string | undefined, surfaces: RoomSurfaces, props: StagedPropView[] | undefined): WallFace {
  const anchors = STAGING_KITS[spaceType ?? '']?.feature;
  if (anchors) {
    // The wall most of the space's own props stand against (the first named counts most).
    const score = new Map<WallFace, number>();
    for (const p of props ?? []) {
      if (p.kind !== 'floor' || !p.def) continue;
      const i = anchors.indexOf(p.def.id);
      if (i < 0) continue;
      const f = faceOfRotation(p.rotation);
      score.set(f, (score.get(f) ?? 0) + (anchors.length - i));
    }
    let best: WallFace | null = null;
    for (const f of FACES) if ((score.get(f) ?? 0) > 0 && (best === null || (score.get(f) ?? 0) > (score.get(best) ?? 0))) best = f;
    return best ?? 'front';
  }
  // The wall with the most garage door.
  let best: WallFace | null = null;
  let most = 0;
  surfaces.walls.forEach((w, i) => {
    const width = (w.openings ?? []).filter((o) => o.kind === 'garageDoor').reduce((sum, o) => sum + o.width, 0);
    if (width > most + 1e-9) { most = width; best = FACES[i]; }
  });
  return best ?? 'front';
}

/** Which way a wall is from the room's centre (room coordinates: x along the length, z along the width; the front wall at z = 0). */
export function towardWall(face: WallFace): [number, number] {
  switch (face) {
    case 'front': return [0, -1];
    case 'back': return [0, 1];
    case 'left': return [-1, 0];
    case 'right': return [1, 0];
  }
}
